import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { PageHeader, Section } from "../components/ui-kit";
import { Lightbox } from "../components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (src) => (src?.startsWith("http") ? src : `${BACKEND}${src}`);

const FILTERS = [
  { id: "all", label: "All photos" },
  { id: "activities", label: "Activities" },
  { id: "trips", label: "Days out" },
  { id: "life-skills", label: "Life skills" },
];

export default function Gallery() {
  const [gallery, setGallery] = useState(null);
  const [filter, setFilter] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setGallery(r.data)).catch(() => setGallery({}));
  }, []);

  const photos =
    gallery === null
      ? []
      : (filter === "all"
          ? [...(gallery.activities || []), ...(gallery.trips || []), ...(gallery["life-skills"] || [])]
          : gallery[filter] || []
        ).map((p) => ({ ...p, src: resolveSrc(p.src) }));

  return (
    <>
      <PageHeader
        eyebrow="Gallery"
        title="Moments from our days together"
        intro="A growing collection of happy moments — activities, days out and life skills sessions with the people we support. Everyone pictured has given their consent to share."
        testid="gallery-header"
      />

      <Section tone="white" data-testid="gallery-page">
        <div className="flex flex-wrap gap-3" role="group" aria-label="Filter photos by category">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={`px-5 py-2.5 rounded-full text-base font-semibold transition-colors ${
                filter === f.id
                  ? "bg-orange-600 text-white"
                  : "bg-white border-2 border-stone-300 text-stone-700 hover:border-orange-600 hover:bg-orange-50"
              }`}
              data-testid={`gallery-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {gallery === null ? (
          <div className="flex items-center gap-2 text-stone-600 py-16">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading photos…
          </div>
        ) : photos.length === 0 ? (
          <p className="py-16 text-lg text-stone-600">No photos in this category yet — check back soon!</p>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {photos.map((p, i) => (
              <button
                key={p.id || i}
                onClick={() => setLightboxIndex(i)}
                className="group overflow-hidden rounded-3xl bg-stone-200 border border-stone-200 aspect-square text-left"
                aria-label={`View photo: ${p.alt}`}
                data-testid={`gallery-photo-${i}`}
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </button>
            ))}
          </div>
        )}
      </Section>

      {lightboxIndex !== null && (
        <Lightbox
          images={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  );
}
